import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { Search, Plus, Edit, Trash2, CheckCircle2, XCircle, AlertCircle, RefreshCw, Tags } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [formData, setFormData] = useState({
    code: "",
    label: "",
    parentId: "",
  });

  const { data: categories, isLoading, isError, refetch } = trpc.admin.categories.list.useQuery({
    active: undefined,
  });

  const createMutation = trpc.admin.categories.create.useMutation({
    onSuccess: () => {
      toast.success("Catégorie créée");
      setCreateDialogOpen(false);
      setFormData({ code: "", label: "", parentId: "" });
      refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const updateMutation = trpc.admin.categories.update.useMutation({
    onSuccess: () => {
      toast.success("Catégorie mise à jour");
      setEditDialogOpen(false);
      setSelectedCategory(null);
      setFormData({ code: "", label: "", parentId: "" });
      refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = trpc.admin.categories.delete.useMutation({
    onSuccess: () => {
      toast.success("Catégorie supprimée");
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const filteredCategories = categories?.filter(
    (cat) =>
      cat.code.toLowerCase().includes(search.toLowerCase()) ||
      cat.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (category: any) => {
    setSelectedCategory(category);
    setFormData({
      code: category.code,
      label: category.label,
      parentId: category.parentId || "",
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (category: any) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Catégories BTP</h1>
            <p className="text-muted-foreground">Gérer la taxonomie des catégories métiers</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setFormData({ code: "", label: "", parentId: "" })}>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle catégorie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer une catégorie</DialogTitle>
                <DialogDescription>Ajouter une nouvelle catégorie à la taxonomie BTP</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="create-code">Code *</Label>
                  <Input
                    id="create-code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="BTP_SSI"
                  />
                </div>
                <div>
                  <Label htmlFor="create-label">Label *</Label>
                  <Input
                    id="create-label"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    placeholder="SSI / Sécurité incendie"
                  />
                </div>
                <div>
                  <Label htmlFor="create-parentId">Catégorie parente (optionnel)</Label>
                  <select
                    id="create-parentId"
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    value={formData.parentId}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                  >
                    <option value="">Aucune</option>
                    {categories
                      ?.filter((c) => c.id !== selectedCategory?.id)
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.label}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Annuler
                </Button>
                <Button
                  onClick={() =>
                    createMutation.mutate({
                      code: formData.code,
                      label: formData.label,
                      parentId: formData.parentId || undefined,
                    })
                  }
                  disabled={!formData.code || !formData.label || createMutation.isPending}
                >
                  Créer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par code ou label..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" />
                <p className="text-muted-foreground font-medium">Erreur de chargement des catégories</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            ) : filteredCategories?.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[200px] gap-2">
                <Tags className="h-8 w-8 text-muted-foreground/30" />
                <p className="text-muted-foreground">
                  {search ? `Aucun résultat pour "${search}"` : "Aucune catégorie dans la taxonomie"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Utilisations</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCategories?.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell className="font-mono text-sm">{category.code}</TableCell>
                      <TableCell className="font-medium">{category.label}</TableCell>
                      <TableCell>
                        {category.active ? (
                          <Badge>
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {category._count?.CompanyProfiles || 0} entreprises,{" "}
                          {category._count?.Qualifications || 0} qualifications
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(category)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier la catégorie</DialogTitle>
              <DialogDescription>Modifier les informations de la catégorie</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-code">Code *</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-label">Label *</Label>
                <Input
                  id="edit-label"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-parentId">Catégorie parente (optionnel)</Label>
                <select
                  id="edit-parentId"
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  value={formData.parentId}
                  onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                >
                  <option value="">Aucune</option>
                  {categories
                    ?.filter((c) => c.id !== selectedCategory?.id)
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={selectedCategory?.active ?? true}
                  onChange={(e) => {
                    if (selectedCategory) {
                      updateMutation.mutate({
                        id: selectedCategory.id,
                        active: e.target.checked,
                      });
                    }
                  }}
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => {
                  if (!selectedCategory) return;
                  updateMutation.mutate({
                    id: selectedCategory.id,
                    code: formData.code,
                    label: formData.label,
                    parentId: formData.parentId || undefined,
                  });
                }}
                disabled={!formData.code || !formData.label || updateMutation.isPending}
              >
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer la catégorie</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer la catégorie "{selectedCategory?.label}" ?
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (selectedCategory) {
                    deleteMutation.mutate({ id: selectedCategory.id });
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
